import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MessageCircle, Send, MapPin, Clock, ArrowRight, Sparkles } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { post } from '@/lib/apiClient';
import { useAuth } from '../../context/AuthContext';

const contactCards = [
  { Icon: Mail, title: 'Sales', desc: 'Talk to our team about plans and pricing.', email: 'sales@leadnova.co', color: 'var(--sky)', bg: 'var(--sky-light)' },
  { Icon: MessageCircle, title: 'Support', desc: 'Get help with your account or technical issues.', email: 'support@leadnova.co', color: 'var(--green)', bg: 'var(--green-light)' },
  { Icon: Phone, title: 'General', desc: 'For partnerships, press, and other inquiries.', email: 'info@leadnova.co', color: 'var(--amber)', bg: 'var(--amber-light)' }
];

export default function ContactPage() {
  const { isAuthenticated } = useAuth();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const isValidEmail = (val: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (e.target.name === 'email' && emailError) setEmailError('');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValidEmail(form.email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    if (!recaptchaToken) {
      setSubmitError('Please complete the reCAPTCHA verification.');
      return;
    }
    setLoading(true);
    setSubmitError(null);
    setSuccess(false);
    try {
      await post('contact', { ...form, recaptchaToken });
      setSuccess(true);
      setForm({ name: '', email: '', subject: '', message: '' });
      setRecaptchaToken(null);
      recaptchaRef.current?.reset();
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ct">
      <section className="ct-hero">
        <div className="ct-hero-bg" />
        <div className="ct-hero-dots" />
        <div className="container ct-hero-inner">
          <div className="ct-hero-pill">
            <Mail size={14} />
            Contact
          </div>
          <h1 className="ct-hero-title">Get in touch</h1>
          <p className="ct-hero-desc">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      <section className="ct-cards-section">
        <div className="container">
          <div className="ct-cards-grid">
            {contactCards.map((c) => {
              const CIcon = c.Icon;
              return (
                <div key={c.title} className="ct-card">
                  <div className="ct-card-icon" style={{ background: c.bg, color: c.color }}>
                    <CIcon size={22} />
                  </div>
                  <h3 className="ct-card-title">{c.title}</h3>
                  <p className="ct-card-desc">{c.desc}</p>
                  <a href={`mailto:${c.email}`} className="ct-card-email">{c.email}</a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="ct-form-section">
        <div className="container">
          <div className="ct-form-layout">
            <div className="ct-form-sidebar">
              <div className="ct-pill" style={{ background: 'var(--sky-light)', color: 'var(--sky)' }}>
                <Sparkles size={14} />
                Send a Message
              </div>
              <h2 className="ct-form-heading">We'd love to hear from you</h2>
              <p className="ct-form-subtext">Fill out the form and we'll get back to you within 24 hours.</p>

              <div className="ct-info-list">
                <div className="ct-info-item">
                  <div className="ct-info-icon"><MapPin size={18} /></div>
                  <div>
                    <div className="ct-info-label">Office</div>
                    <div className="ct-info-value">Suite 100, 10394-105st, Edmonton, Alberta, Canada 94105</div>
                  </div>
                </div>
                <div className="ct-info-item">
                  <div className="ct-info-icon"><Clock size={18} /></div>
                  <div>
                    <div className="ct-info-label">Business Hours</div>
                    <div className="ct-info-value">Mon-Fri, 9AM-6PM PST</div>
                  </div>
                </div>
                <div className="ct-info-item">
                  <div className="ct-info-icon"><Phone size={18} /></div>
                  <div>
                    <div className="ct-info-label">Phone</div>
                    <div className="ct-info-value">(415) 555-0123</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="ct-form-card">
              {success && (
                <div className="ct-success-banner">
                  Thank you! We'll be in touch soon.
                </div>
              )}
              {submitError && (
                <div className="ct-error-banner">
                  {submitError}
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="ct-form-row">
                  <div className="ct-form-group">
                    <label className="ct-label">Name</label>
                    <input type="text" name="name" value={form.name} onChange={handleChange} className="ct-input" placeholder="Your name" required />
                  </div>
                  <div className="ct-form-group">
                    <label className="ct-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      onBlur={() => { if (form.email && !isValidEmail(form.email)) setEmailError('Please enter a valid email address.'); }}
                      className={`ct-input${emailError ? ' ct-input-error' : ''}`}
                      placeholder="you@company.com"
                      required
                    />
                    {emailError && <p className="ct-field-error">{emailError}</p>}
                  </div>
                </div>
                <div className="ct-form-group">
                  <label className="ct-label">Subject</label>
                  <select name="subject" value={form.subject} onChange={handleChange} className="ct-input" required>
                    <option value="">Select a subject</option>
                    <option value="sales">Sales inquiry</option>
                    <option value="support">Technical support</option>
                    <option value="billing">Billing question</option>
                    <option value="partnership">Partnership opportunity</option>
                    <option value="press">Press inquiry</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="ct-form-group">
                  <label className="ct-label">Message</label>
                  <textarea name="message" value={form.message} onChange={handleChange} className="ct-textarea" placeholder="How can we help?" rows={5} required />
                </div>
                <div className="ct-recaptcha-wrapper">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                    onChange={(token) => setRecaptchaToken(token)}
                    onExpired={() => setRecaptchaToken(null)}
                  />
                </div>
                <button type="submit" className="ct-submit-btn" disabled={loading || !recaptchaToken}>
                  {loading ? 'Sending...' : <><Send size={18} /> Send Message</>}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="ct-faq-banner">
        <div className="container ct-faq-inner">
          <p className="ct-faq-text">Looking for answers? Check our frequently asked questions.</p>
          <Link to="/pricing" className="ct-faq-link">
            View FAQ <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <section className="ct-cta-section">
        <div className="ct-cta-glow" />
        <div className="container ct-cta-inner">
          <h2 className="ct-cta-title">Ready to get started?</h2>
          <p className="ct-cta-desc">Start your 14-day free trial. No credit card required.</p>
          {!isAuthenticated && (
            <Link to="/pricing" className="ct-cta-btn">
              Start Free Trial <ArrowRight size={18} />
            </Link>
          )}
        </div>
      </section>

      <style>{`
        .ct { overflow-x: hidden; }

        .ct-hero {
          background: var(--navy); position: relative;
          padding: 140px 0 80px; text-align: center; overflow: hidden;
        }
        .ct-hero-bg {
          position: absolute; inset: 0;
          background: radial-gradient(ellipse 80% 60% at 50% 0%, rgba(77,184,232,0.18), transparent 70%);
        }
        .ct-hero-dots {
          position: absolute; inset: 0; opacity: 0.03;
          background-image: radial-gradient(circle, #fff 1px, transparent 1px);
          background-size: 32px 32px;
        }
        .ct-hero-inner { position: relative; z-index: 1; }
        .ct-hero-pill {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(77,184,232,0.12); border: 1px solid rgba(77,184,232,0.25);
          border-radius: 100px; padding: 6px 18px; margin-bottom: 24px;
          font-size: 13px; font-weight: 600; color: var(--sky);
        }
        .ct-hero-title {
          font-size: 52px; font-weight: 800; color: #fff;
          line-height: 1.1; margin: 0 auto 20px; letter-spacing: -0.03em;
        }
        .ct-hero-desc {
          font-size: 19px; color: rgba(255,255,255,0.6);
          max-width: 560px; margin: 0 auto; line-height: 1.7;
        }

        .ct-cards-section { padding: 64px 0 0; }
        .ct-cards-grid {
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px;
        }
        .ct-card {
          background: #fff; border-radius: 16px; padding: 32px 24px;
          text-align: center; border: 1px solid var(--gray-100);
          box-shadow: 0 2px 12px rgba(0,0,0,0.03);
          transition: all 0.3s;
        }
        .ct-card:hover { box-shadow: 0 8px 28px rgba(0,0,0,0.06); transform: translateY(-3px); }
        .ct-card-icon {
          width: 52px; height: 52px; border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          margin: 0 auto 16px;
        }
        .ct-card-title { font-size: 18px; font-weight: 700; color: var(--navy); margin-bottom: 8px; }
        .ct-card-desc { font-size: 14px; color: var(--gray-500); line-height: 1.6; margin-bottom: 14px; }
        .ct-card-email { font-size: 15px; font-weight: 600; color: var(--sky); text-decoration: none; }
        .ct-card-email:hover { text-decoration: underline; }

        .ct-form-section { padding: 64px 0 80px; }
        .ct-form-layout {
          display: grid; grid-template-columns: 1fr 1.3fr; gap: 48px; align-items: start;
        }

        .ct-pill {
          display: inline-flex; align-items: center; gap: 8px;
          border-radius: 100px; padding: 6px 16px; margin-bottom: 16px;
          font-size: 13px; font-weight: 600;
        }
        .ct-form-heading {
          font-size: 30px; font-weight: 800; color: var(--navy);
          margin-bottom: 12px; letter-spacing: -0.02em; line-height: 1.2;
        }
        .ct-form-subtext { font-size: 16px; color: var(--gray-500); line-height: 1.6; margin-bottom: 32px; }

        .ct-info-list { display: flex; flex-direction: column; gap: 20px; }
        .ct-info-item { display: flex; align-items: flex-start; gap: 14px; }
        .ct-info-icon {
          width: 40px; height: 40px; border-radius: 10px;
          background: var(--gray-50); color: var(--sky);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; border: 1px solid var(--gray-100);
        }
        .ct-info-label { font-size: 13px; color: var(--gray-500); font-weight: 500; margin-bottom: 2px; }
        .ct-info-value { font-size: 15px; color: var(--navy); font-weight: 600; }

        .ct-form-card {
          background: #fff; border-radius: 20px; padding: 36px;
          border: 1px solid var(--gray-200);
          box-shadow: 0 4px 24px rgba(0,0,0,0.05);
        }
        .ct-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
        .ct-form-group { margin-bottom: 16px; }
        .ct-label {
          display: block; font-size: 14px; font-weight: 600;
          color: var(--gray-900); margin-bottom: 6px;
        }
        .ct-input, .ct-textarea {
          width: 100%; padding: 11px 14px; border-radius: 10px;
          border: 1px solid var(--gray-200); font-size: 15px;
          font-family: inherit; color: var(--gray-900);
          background: var(--gray-50); transition: all 0.2s;
          outline: none; box-sizing: border-box;
        }
        .ct-input:focus, .ct-textarea:focus {
          border-color: var(--sky); background: #fff;
          box-shadow: 0 0 0 3px rgba(77,184,232,0.1);
        }
        .ct-input-error { border-color: var(--red) !important; }
        .ct-input-error:focus { box-shadow: 0 0 0 3px rgba(239,68,68,0.1) !important; }
        .ct-field-error { margin: 4px 0 0; font-size: 12px; color: var(--red); }
        .ct-textarea { resize: vertical; min-height: 120px; }

        .ct-recaptcha-wrapper {
          display: flex; justify-content: center;
          margin-bottom: 16px;
        }

        .ct-submit-btn {
          display: flex; align-items: center; justify-content: center;
          gap: 8px; width: 100%; padding: 14px 24px;
          background: var(--green); color: #fff; border: none;
          border-radius: 10px; font-size: 16px; font-weight: 600;
          font-family: inherit; cursor: pointer;
          box-shadow: 0 4px 14px rgba(58,170,53,0.3);
          transition: all 0.2s;
        }
        .ct-submit-btn:hover { background: #2e9229; }
        .ct-submit-btn:disabled { opacity: 0.65; cursor: not-allowed; }
        .ct-success-banner {
          background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px;
          color: #15803d; font-size: 15px; font-weight: 600;
          padding: 14px 18px; margin-bottom: 20px; text-align: center;
        }
        .ct-error-banner {
          background: #fef2f2; border: 1px solid #fecaca; border-radius: 10px;
          color: #dc2626; font-size: 14px; font-weight: 500;
          padding: 14px 18px; margin-bottom: 20px;
        }

        .ct-faq-banner {
          background: var(--gray-50); padding: 40px 0;
          border-top: 1px solid var(--gray-100);
          border-bottom: 1px solid var(--gray-100);
        }
        .ct-faq-inner {
          display: flex; align-items: center; justify-content: center;
          gap: 16px; flex-wrap: wrap;
        }
        .ct-faq-text { font-size: 16px; color: var(--gray-500); margin: 0; }
        .ct-faq-link {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 15px; font-weight: 600; color: var(--sky);
          text-decoration: none;
        }
        .ct-faq-link:hover { text-decoration: underline; }

        .ct-cta-section {
          background: var(--navy); padding: 80px 0;
          text-align: center; position: relative; overflow: hidden;
        }
        .ct-cta-glow {
          position: absolute; inset: 0;
          background: radial-gradient(ellipse 60% 50% at 50% 50%, rgba(77,184,232,0.12), transparent);
        }
        .ct-cta-inner { position: relative; z-index: 1; }
        .ct-cta-title { font-size: 38px; font-weight: 800; color: #fff; margin-bottom: 16px; letter-spacing: -0.02em; }
        .ct-cta-desc { font-size: 18px; color: rgba(255,255,255,0.6); max-width: 500px; margin: 0 auto 32px; }
        .ct-cta-btn {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--green); color: #fff;
          font-size: 16px; font-weight: 700; padding: 14px 32px;
          border-radius: 10px; text-decoration: none;
          box-shadow: 0 4px 14px rgba(58,170,53,0.4); transition: all 0.2s;
        }
        .ct-cta-btn:hover { text-decoration: none; opacity: 0.9; }

        /* ========== TABLET (768px) ========== */
        @media (max-width: 768px) {
          .ct-hero { padding: 120px 0 60px; }
          .ct-hero-title { font-size: 36px; }
          .ct-hero-desc { font-size: 16px; }

          .ct-cards-section { padding: 48px 0 0; }
          .ct-cards-grid { grid-template-columns: 1fr; max-width: 400px; margin: 0 auto; gap: 14px; }
          .ct-card { padding: 24px 20px; }

          .ct-form-section { padding: 48px 0 56px; }
          .ct-form-layout { grid-template-columns: 1fr; gap: 36px; }
          .ct-form-heading { font-size: 26px; }
          .ct-form-card { padding: 28px 24px; }

          .ct-cta-title { font-size: 28px; }
          .ct-cta-desc { font-size: 16px; }
        }

        /* ========== SMALL MOBILE (480px) ========== */
        @media (max-width: 480px) {
          .ct-hero { padding: 110px 0 48px; }
          .ct-hero-title { font-size: 28px; }
          .ct-hero-desc { font-size: 15px; }

          .ct-cards-section { padding: 36px 0 0; }
          .ct-cards-grid { max-width: 100%; }

          .ct-form-section { padding: 36px 0 40px; }
          .ct-form-heading { font-size: 22px; }
          .ct-form-subtext { font-size: 15px; margin-bottom: 24px; }
          .ct-form-card { padding: 24px 18px; border-radius: 16px; }
          .ct-form-row { grid-template-columns: 1fr; gap: 0; }

          .ct-faq-inner { flex-direction: column; text-align: center; gap: 8px; }
          .ct-faq-text { font-size: 15px; }

          .ct-cta-section { padding: 56px 0; }
          .ct-cta-title { font-size: 24px; }
          .ct-cta-desc { font-size: 15px; }
          .ct-cta-btn { padding: 12px 24px; font-size: 15px; }
        }
      `}</style>
    </div>
  );
}
